import React, { Component } from 'react';
import { BrowserRouter as Router, Route, Switch, withRouter} from 'react-router-dom';
import Login from './components/Login/Login';
import Logout from './components/Logout/logout';
import { connect } from 'react-redux';
import config from './utils/config';
import { ThemeProvider } from 'styled-components';
import { theme } from 'cdk-radial';
import './app.scss';

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { Security, LoginCallback, SecureRoute } from "@okta/okta-react";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { isTokenValid, clearSessionAndReload } from './utils/utils';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { AhaWidget } from '@cdk-prod/fortellis-aha-widget';
import {
  FFContextProvider
} from '@harnessio/ff-react-client-sdk';
import { environmentURLs } from './components/common/CaptureEnvironment';
import {CheckDaabAccessFeatureFlag} from './utils/harnessFeatureFlags';
import DPErrorBoundary from './components/error-boundary/DPErrorBoundary';
import { loadWalkMeScript,updateFortellisWalkMeData } from './utils/walkMeHelper';
import { trackPageView, getPageDataFromPath } from './utils/gtm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const applicationId = process.env.REACT_APP_PLATFORM_APPLICATIONID_DEALERPORTAL;
const isAhaEnabled =
  process.env.REACT_APP_FEATURE_AHA_WIDGET_ENABLED === 'true';

const errorBoundaryProps = {
  app: 'subscription-portal',
  clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
  telemetryUrl: process.env.REACT_APP_CRASH_TELEMETRY_ANON_URL,
  userTelemetryUrl: process.env.REACT_APP_CRASH_TELEMETRY_USER_URL
};

const AhaWidgetWithAuth = withAuth(AhaWidget);
const reactTarget = { name: 'ReactClientSDK', identifier: 'reactclientsdk' };

class Root extends Component{
  constructor(props){
    super(props);
    this.loginError = false;
    this.state = {
      gtmInitialPageviewSent: false
    };
     this.oktaAuth = new OktaAuth({
      issuer: process.env.REACT_APP_AUTH_ISSUER,
      clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
      redirectUri: window.location.origin,
      authorizeUrl: process.env.REACT_APP_AUTH_URL,
      tokenUrl: process.env.REACT_APP_TOKEN_URL
    });
    this.restoreOriginalUri = async (_oktaAuth, originalUri) => {
      const returnUrl = originalUri || "/";
      localStorage.setItem("returnUrl", returnUrl);
      props.history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };
    // Store previous user data to compare in componentDidUpdate
    this.prevUserData = null;
  }

  async componentDidMount(){
    if(!isTokenValid()) {
      clearSessionAndReload();
    }
    if(process.env.REACT_APP_WALKME_SRC)
    {
    loadWalkMeScript({ walkMeSrc: process.env.REACT_APP_WALKME_SRC });
    }
    this.updateWalkMeData();

    // Listen for route changes
    this.unlisten = this.props.history.listen((location) => {
      this.trackGtmPageView(location);
    });

    // Check if we can track initial page view immediately
    this.checkAndTrackInitialGtmPageView();
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  trackGtmPageView(location) {
    const { loginDetailsSlice } = this.props;
    const { isAuthenticated, entityDetails, errorInGettingOrgName } = loginDetailsSlice || {};
    const orgName = entityDetails?.organizationName;

    // Do not track until user is authenticated and organization details are fetched
    if (!isAuthenticated || (!orgName && !errorInGettingOrgName)) {
      return;
    }

    const pageData = getPageDataFromPath(null, location.pathname);
    if (!pageData.disableGlobalTracking) {
      trackPageView(pageData, location.pathname);
    }
  }

  checkAndTrackInitialGtmPageView() {
    const { loginDetailsSlice } = this.props;
    const { isAuthenticated, entityDetails, errorInGettingOrgName } = loginDetailsSlice || {};
    const orgName = entityDetails?.organizationName;

    // Conditions to fire the initial pageview:
    // 1. User is authenticated
    // 2. Organization name is available OR the fetch has failed/errored (so we don't wait forever)
    if (!this.state.gtmInitialPageviewSent && isAuthenticated && (orgName || errorInGettingOrgName)) {
      this.trackGtmPageView(this.props.location);
      this.setState({ gtmInitialPageviewSent: true });
    }
  }

  componentDidUpdate(prevProps) {
    const currentUserData = this.getUserData();
    const prevUserData = this.getUserAndOrgDataFromProps(prevProps);
    
    // Only update WalkMe if user data has actually changed
    if (JSON.stringify(currentUserData) !== JSON.stringify(prevUserData)) {
      this.updateWalkMeData();
    }

    // Check for deferred GTM pageview
    this.checkAndTrackInitialGtmPageView();
  }

  getUserAndOrgDataFromProps(props) {
    const { decodedToken = {} } = props.loginDetailsSlice || {};
    const userName = `${decodedToken.firstName || ''} ${decodedToken.lastName || ''}`.trim();

    return {
      email: props.loginDetailsSlice?.userEmail,
      orgId: props.loginDetailsSlice?.fortellisEnterpriseEntityId,
      userName: userName,
      orgName: props.loginDetailsSlice?.entityDetails?.organizationName,
      fortellisDefinedType: props.loginDetailsSlice?.entityDetails?.fortellisDefinedType,
    };
  }

  getUserData() {
    return this.getUserAndOrgDataFromProps(this.props);
  }

  updateWalkMeData() {
    const data = this.getUserData();
    
    // Only update if we have valid user data
    if (data.email && data.orgId) {
      updateFortellisWalkMeData({
        data
      });
      
      // Store for future comparison
      this.prevUserData = data;
    }
  }

  render() {
    if (config.env !== 'prod') {
      console.warn(
        `Application is currently running in '${config.env}' mode`,
        config
      );
    }

    if (window.location.pathname === '/redirecting') {
      return (
        <div className={'App__redirecting'}>
          <strong>Redirecting...</strong>
        </div>
      );
    }


    setInterval(()=>{
      if(window.location.pathname === '/' && !this.loginError){
        window.location.href = `${window.location.origin}/login`
      }
    }, 6000);

    return (
      <ThemeProvider theme={theme}>
        <DPErrorBoundary {...errorBoundaryProps}>
          <Elements stripe={stripePromise}>
            <FFContextProvider apiKey={environmentURLs?.REACT_APP_DAAB_HARNESS_CLIENT_SDK_KEY} target={reactTarget}>
              <CheckDaabAccessFeatureFlag />
            </FFContextProvider>
            <Security oktaAuth={this.oktaAuth} restoreOriginalUri={this.restoreOriginalUri}>
              {isAhaEnabled && (
                <AhaWidgetWithAuth
                  applicationId={applicationId}
                />
              )}
              <Switch>
                <SecureRoute
                  exact path={'/dp/:solutionId/:selectedPlanId'}
                  component={Login}
                />
                <SecureRoute
                  exact path={'/dp/:solutionId'}
                  component={Login}
                />
                <SecureRoute exact path={'/login'} component={Login} />
                <SecureRoute exact path={'/logout'} component={Logout} />

                <Route exact path={'/'} component={(props)=>(
                    <LoginCallback  {...props} errorComponent={({error})=>{
                        this.loginError = true;
                        return <div>{error?.errorSummary}</div>
                    }}/>)} />
                <SecureRoute path="*" component={Login} />
              </Switch>
            </Security>
          </Elements>
        </DPErrorBoundary>
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state) => {return state};
const ConnectedRoot = connect(mapStateToProps)(Root);
const RootWithRouterAccess = withRouter(ConnectedRoot);

export default class App extends Component {
  render() {
    return (<Router><RootWithRouterAccess/></Router>);
  }
}
